import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { Loader2, GitMerge, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

function Group({ g, idx, busy, onMerge, onIgnore }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-[#1E293B] p-3" data-testid={`duplicate-group-${idx}`}>
      <div className="space-y-2">
        {g.cards.map((c, i) => (
          <div key={c.id} className="flex items-start gap-2 text-sm">
            <span className={`mt-0.5 text-[10px] px-1.5 py-0.5 rounded shrink-0 ${i === 0 ? "bg-blue-600/20 text-blue-300" : "bg-slate-700/60 text-slate-400"}`}>{i === 0 ? "Newest" : "Older"}</span>
            <div className="min-w-0">
              <p className="text-white truncate">{c.name || "Unknown"}{c.company ? <span className="text-slate-400"> · {c.company}</span> : null}</p>
              <p className="text-xs text-slate-400 truncate">{[c.phone || c.phone2, c.email || c.email2].filter(Boolean).join(" · ") || "No phone or email"}{(c.updated_at || c.created_at) ? ` · ${fmt(c.updated_at || c.created_at)}` : ""}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid={`duplicate-merge-button-${idx}`} disabled={!!busy} onClick={() => onMerge(g)}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold transition-colors">
          {busy === g.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />} Merge {g.cards.length}
        </button>
        <button data-testid={`duplicate-ignore-button-${idx}`} disabled={!!busy} onClick={() => onIgnore(g)}
          className="px-3 py-2 rounded-lg border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 disabled:opacity-50 text-xs transition-colors">
          Not duplicates
        </button>
      </div>
    </div>
  );
}

export default function DuplicatesDialog({ open, onClose, onChanged }) {
  const [groups, setGroups] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setGroups(null);
    api.get("/cards/duplicates").then(({ data }) => setGroups(data.groups)).catch(() => { setGroups([]); toast.error("Couldn't check for duplicates"); });
  }, [open]);

  const act = async (g, kind) => {
    setBusy(g.key);
    try {
      const ids = g.cards.map((c) => c.id);
      await api.post(kind === "merge" ? "/cards/merge" : "/cards/duplicates/ignore", { ids });
      setGroups((prev) => prev.filter((x) => x.key !== g.key));
      toast.success(kind === "merge" ? `Merged into ${g.cards[0].name || "one card"}` : "Got it, we won't suggest these again");
      if (kind === "merge") onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-md" testId="duplicates-dialog">
      <div className="p-6 flex flex-col max-h-[85vh]">
        <p className="overline text-blue-500 mb-1">Tidy up</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Possible duplicates</h2>
        <p className="text-sm text-slate-400 mb-4">Cards sharing a name, email or phone. Merging keeps the newest details and fills gaps from the older card.</p>
        <div className="flex-1 overflow-y-auto space-y-3 -mx-1 px-1" data-testid="duplicates-list">
          {groups === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="duplicates-empty">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" /> No duplicates found
            </div>
          ) : (
            groups.map((g, i) => <Group key={g.key} g={g} idx={i} busy={busy} onMerge={(x) => act(x, "merge")} onIgnore={(x) => act(x, "ignore")} />)
          )}
        </div>
      </div>
    </Modal>
  );
}
